/* eslint-disable */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal, Skeleton } from "@aqar-app/react-ui";
import { InitialResolver } from "../../../lib/use.middlewares";
import { AuthResolver } from "@/lib/serverOnly";
import { Filter } from "@/lib/path-resolver-middlewares/filters";
import { Category } from "@/lib/path-resolver-middlewares/category";
import { City } from "@/lib/path-resolver-middlewares/city";
import { Direction } from "@/lib/path-resolver-middlewares/direction";
import { District } from "@/lib/path-resolver-middlewares/district";
import { Listing } from "@/lib/path-resolver-middlewares/listing";
import { Filters } from "@/components/filters/filters";
import css from "../.module.scss";
import { useFindDailyListings } from "@/components/new-resolver/useFindListings";
import { CategoryList } from "@/components/new-resolver/CountersButtons";
import AppLayout from "../../../containers/layouts/AppLayout";
import { usePath } from "@/contexts/path/path";
import { cityGeolocations } from "@/common/cityGeolocations";
import {
  createHtmlCityMarker,
  createHtmlListingMarker,
  Map,
  restriction,
  useBounds,
} from "@/components/map/map";
import { formatPrice } from "@/components/marker/Marker";
import logger from "../../../../console";
import Title from "../../../components/Title";
import dayjs from "dayjs";

import { DailyListingCard } from "../../../components/Listing/ListingCard";
import { FreeMapOptions, FrozenMapOptions } from "../[[...params]]";
import { FloatingDailyListingDetails } from "@/components/Listing/DailyListingScreen/floating.map";
import { List, MapControls } from "@/src/components/map/screen.components";
import WithAuthentication from "@/src/hoc/withAuthentication";
import dynamic from "next/dynamic";
import Icon from "@/components/icon";
import {
  trackGenericCTAClickEvent,
  trackListingsList,
} from "@/lib/tracking/trackNavigationEvents";
import { PickerHolder } from "@/components/Listing/DailyListingScreen/pickerHolder";
import {
  listingToEECItemObject,
  trackSelectItem,
  trackViewItemList,
  trackViewProperties,
} from "@/lib/tracking/trackEECEvents";

const DailyDatePickerPopup = dynamic(
  () =>
    import("../../../components/Listing/DailyListingScreen/datepicker").then(
      (m) => m.DailyDatePickerPopup,
    ),
  { ssr: false, loading: PickerHolder },
);
export const defaultProps = {
  center: {
    // riyadh
    lat: 24.7136,
    lng: 46.6753,
  },
  zoom: 11,
};

export function MapScreen() {
  const {
    updateContext,
    daily_renting_filter,
    categories,
    categoriesDictionary,
    frozenContext,
    ...path
  } = usePath();
  const { listing, listingType, category, district, city, direction, filters } =
    path;

  const markersDictionary = useRef({});
  const citiesMarker = useMemo(() => {
    if (typeof window === "undefined") return [];
    return cityGeolocations.map((city, id) => ({
      html: createHtmlCityMarker({
        text: city.name.ar,
        className: `${css.marker} ${css.city} ${css.daily}`,
      }),
      city,
      id: `${id}city`,
    }));
  }, []);
  const previousListings = useRef([]);
  const [map, setMap] = useState();
  const [setIsDrawing] = useState(false);
  const [mode, setMode] = useState("DEFAULT" /*DRAWING|FLOATING_LIST*/);
  const [mapType, setMapType] = useState("roadmap");
  const bounds = useBounds();
  const activeBoundsRef = useRef();
  const [currentListing, setCurrentListing] = useState();
  const [fullListing, setFullListing] = useState();
  const listingDetailsRef = useRef();
  const [cityZoomLevel, setCityZoomLevel] = useState(false);
  const allCitiesBounds = useMemo(() => {
    if (typeof window === "undefined" || typeof google === "undefined")
      return undefined;
    const bounds = new window.google.maps.LatLngBounds();
    for (const { lat, lng } of cityGeolocations) {
      bounds.extend({ lat, lng });
    }
    return bounds;
  }, [map]);

  const zoomRef = useRef({
    inProgress: false,
    value: 0,
    target: 0,
    direction: "in",
  });

  const [listings, { loading, total, fetchMore }] = useFindDailyListings(
    {},
    () => {
      if (!map || path.basePath !== "/map/daily-rent") return;
      trackViewItemList({
        itemListName: "daily rent listings",
        listingType: "daily",
        category: category?.en,
        cityName: city?.name_en,
        directionName: direction?.name_en,
        districtName: district?.name_en,
        filters,
        count: total,
        listings: (listings || []).map((listing, index) =>
          listingToEECItemObject(listing, index, categoriesDictionary),
        ),
      });
      trackViewProperties({
        listingType: "daily",
        viewType: "map",
      });
    },
  );

  // useResolvePathForBackAndForward();

  const filtersRef = useRef({});
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const sharedSelectItemEvent = (listing) => {
    trackSelectItem({
      itemListName: "Map Daily Listings",
      listingType: category?.ga_listing_type || listingType,
      category: (category || categoriesDictionary["0"])?.en,
      cityName: city?.name_en,
      directionName: direction?.name_en,
      districtName: district?.name_en,
      filters: filtersRef.current,
      count: total,
      listings: [listing].map((listing, index) =>
        listingToEECItemObject(listing, index, categoriesDictionary),
      ),
    });
  };

  useEffect(() => {
    if (loading || !map || map?.getZoom() <= 9) return;
    if (!listing) Modal.close();
    if (!google.maps.marker?.AdvancedMarkerElement) return;
    const newListingsDictionary = listings.reduce(
      (aac, li) => ({ ...aac, [li.id]: li }),
      {},
    );

    function onClick(event, listing) {
      updateContext({ skipRouting: true, currentFrozenIndex: listing.index });
      setCurrentListing(listing);
      trackGenericCTAClickEvent("listings_map_view", {
        property_category: category?.en,
        listing_type: listingType,
      });
      sharedSelectItemEvent(listing);
    }

    for (const listing of previousListings.current) {
      if (newListingsDictionary[listing]) continue;
      markersDictionary.current[listing]?.setMap(null);
      markersDictionary.current[listing] = null;
    }
    for (const listing of listings) {
      let period = "يومي";

      const price = listing.range_price || listing.original_range_price;
      if (
        listing.extended_details?.minimum_booking_days >= 7 &&
        listing.extended_details?.minimum_booking_days < 28
      ) {
        period = "أسبوعي";
      } else if (listing.extended_details?.minimum_booking_days >= 28) {
        period = "شهري";
      }
      if (markersDictionary.current[listing.id]) {
        markersDictionary.current[listing.id].content = createHtmlListingMarker(
          {
            text: price ? formatPrice(price) : period,
            className: `${css.marker} ${css.daily} ${css.listing}`,
          },
        );
        continue;
      }

      const markerDivElement = createHtmlListingMarker({
        text: price ? formatPrice(price) : period,
        className: `${css.marker} ${css.daily} ${css.listing}`,
      });

      markersDictionary.current[listing.id] =
        new google.maps.marker.AdvancedMarkerElement({
          map,
          position: listing.location,
          content: markerDivElement,
        });

      markersDictionary.current[listing.id].addListener("click", (e) =>
        onClick(e, listing),
      );
    }
    previousListings.current = listings.map((l) => l.id);

    return () => {};
  }, [listings, loading, map]);

  useEffect(() => {
    if (!currentListing) return;
    updateContext({
      dailyData: daily_renting_filter?.day_date
        ? {
            price: {
              total_price: currentListing.original_range_price || 0,
              discounted_price: currentListing.range_price || 0,
              discount_reason: currentListing.daily_renting_discount_reason,
            },
          }
        : undefined,
    });
  }, [currentListing]);
  useEffect(() => {
    return () => {
      logger.log("Cleanup");
      const keys = Object.keys(markersDictionary.current);
      for (const string of keys) {
        markersDictionary.current[string]?.setMap(null);
        markersDictionary.current[string] = undefined;
      }
    };
  }, []);

  const travelToBounds = (_bounds) => {
    const bounds = new window.google.maps.LatLngBounds(
      _bounds.southWest,
      _bounds.northEast,
    );
    activeBoundsRef.current = bounds;

    const { north, east, south, west } = bounds.toJSON();
    const newPolygon = [
      { lat: north, lng: east },
      { lat: south, lng: east },
      { lat: south, lng: west },
      { lat: north, lng: west },
    ];

    map.fitBounds(bounds, 0);
    map.setZoom(11);
  };

  useEffect(
    function TravelToDistrict() {
      if (!map || !bounds) return;

      travelToBounds(bounds);
    },
    [map, bounds],
  );

  const onMapChange = useCallback(() => {
    /* abort fetch listings if map is not ready
     *  or drawing mode or the zoom<9 [cities markers mode]
     * */
    function fetchPolygon() {
      const coords = map?.getBounds()?.toJSON();
      if (!coords) return;
      const { north, east, south, west } = coords;

      const polygon = [
        { lat: north, lng: east },
        { lat: south, lng: east },
        { lat: south, lng: west },
        {
          lat: north,
          lng: west,
        },
      ];
      if (
        activeBoundsRef.current &&
        !map.getBounds().intersects(activeBoundsRef.current)
      )
        updateContext({
          polygon,
          city: null,
          district: null,
          direction: null,
        });
      else updateContext({ polygon });
    }

    if (zoomRef.current.inProgress) {
      zoomRef.current.inProgress = false;
      if (zoomRef.current.direction === "in") {
        logger.log("Zoom End");
        map.setOptions({
          ...FreeMapOptions,
          restriction,
        });
        setCityZoomLevel(false);
      }
    }

    if (!map || mode === "DRAWING" || map?.getZoom() <= 9) return;
    fetchPolygon();
  }, [map, updateContext, mode]);

  function animateZoomIn() {
    zoomRef.current.direction = "in";
    zoomRef.current.value += 1;
    map.setZoom(zoomRef.current.value);
    map.setCenter(zoomRef.current.center);
    if (zoomRef.current.value >= zoomRef.current.target) {
      zoomRef.current.value = 0;
      return;
    }
    setTimeout(animateZoomIn, 200);
  }

  useEffect(() => {
    function open() {
      trackListingsList(path, listings, { event: "open_listing_list_filters" });
    }

    document.addEventListener("opensearchbar", open);
    return () => {
      document.removeEventListener("opensearchbar", open);
    };
  }, [path, listings]);
  useEffect(() => {
    if (!map) return;

    const listeners = [map.addListener("idle", onMapChange)];

    return () => {
      for (const listener of listeners) {
        listener.remove();
      }
    };
  }, [map, updateContext, mode]);
  const removeCitiesMarkers = useCallback(() => {
    for (const citiesMarkerElement of citiesMarker) {
      if (!markersDictionary.current[citiesMarkerElement.id]) continue;
      markersDictionary.current[citiesMarkerElement.id]?.setMap(null);
      markersDictionary.current[citiesMarkerElement.id] = null;
    }
  }, [citiesMarker]);

  const onZoom = useCallback(() => {
    if (zoomRef.current.inProgress) return;
    const zoom = map.getZoom();
    if (zoom < 9) {
      /*
       * first remove all listings marker
       * then we draw cities marker
       * */
      setCityZoomLevel(true);

      const ls = previousListings.current.concat(...listings);
      for (const listing of ls) {
        markersDictionary.current[listing]?.setMap(null);
        markersDictionary.current[listing] = null;
      }

      for (const { html, city, id } of citiesMarker) {
        if (markersDictionary.current[id]) continue;
        const marker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat: city.display_lat, lng: city.display_lng },
          content: html,
        });
        markersDictionary.current[id] = marker;
        marker.addListener("click", () => {
          removeCitiesMarkers();
          map.setCenter({ lat: city.lat, lng: city.lng });
          zoomRef.current.center = { lat: city.lat, lng: city.lng };
          zoomRef.current.inProgress = true;
          zoomRef.current.target = 10;
          zoomRef.current.value = map.getZoom();
          animateZoomIn();
        });
      }

      if (!zoomRef.current.inProgress && zoom !== 6) {
        logger.log("Zooming", map.getZoom());

        map.setOptions(FrozenMapOptions);
        zoomRef.current.inProgress = true;
        zoomRef.current.direction = "out";
        setTimeout(() => {
          map.fitBounds(allCitiesBounds, 0);
          map.setOptions(FrozenMapOptions);
        }, 50);
      }
    }
  }, [map, removeCitiesMarkers, allCitiesBounds, listings]);

  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("zoom_changed", onZoom);
    return () => listener.remove();
  }, [onZoom, map]);

  return (
    <>
      <Title />
      <div className={css.fullScreen}>
        <div className={css.mapWrapper}>
          <Map
            center={defaultProps.center}
            zoom={defaultProps.zoom}
            onLoad={(map) => {
              map.addListener("click", () => setCurrentListing(undefined));
              setMap(map);
            }}
          />
        </div>
        {mode === "FLOATING_LIST" && (
          <List
            setListing={(listing) => {
              setFullListing(true);
              setCurrentListing(listing);
              sharedSelectItemEvent(listing);
            }}
            listings={listings}
            total={total}
            loading={loading}
            fetchMore={fetchMore}
          />
        )}
        {map && !cityZoomLevel && (
          <MapControls
            mapType={mapType}
            map={map}
            setMapType={setMapType}
            mode={mode}
            setMode={setMode}
          >
            <button
              onClick={Modal.open(<DailyDatePickerPopup />)}
              className={css.picker}
            >
              {daily_renting_filter?.day_date ? (
                <>
                  <Icon width={15} height={15} name="close-bordered-red" />

                  <span>
                    {dayjs(daily_renting_filter.day_date.gte).format("DD MMM")}-
                    {dayjs(daily_renting_filter.day_date.lte).format("DD MMM")}
                  </span>
                </>
              ) : (
                <>
                  <Icon width={15} height={15} name="dailyRent-colored" />
                  <span>اختر تاريخ </span>
                </>
              )}
            </button>
          </MapControls>
        )}

        {!cityZoomLevel && (
          <div className={css.categories}>
            <CategoryList />
          </div>
        )}

        {!cityZoomLevel && (
          <div className={css.total}>
            {loading ? (
              <Skeleton className={css.loading} />
            ) : total ? (
              <span>
                {total > 20 ? 20 : total} من {total} إعلان، قرب أكثر لجميع
                الإعلانات
              </span>
            ) : (
              <span>لا يوجد عقارات هنا. الرجاء تغيير البحث</span>
            )}
          </div>
        )}
        {!fullListing && currentListing && mode !== "FLOATING_LIST" && (
          <div className={css.listingContainer}>
            <DailyListingCard
              onClick={(e, currentFrozenIndex) => {
                updateContext({ currentFrozenIndex });
                setFullListing(true);
              }}
              listing={currentListing}
            />
          </div>
        )}
        {fullListing && currentListing && (
          <div ref={listingDetailsRef} className={css.fullListing}>
            <FloatingDailyListingDetails
              frozenContext={frozenContext}
              onListingChange={(listing) => {
                if (!listing) setFullListing(false);
                setCurrentListing(listing);
                listingDetailsRef.current.scroll(0, 0);
              }}
              frozenListings={listings}
              listing={currentListing}
            />
          </div>
        )}
      </div>
    </>
  );
}

const MapPage = WithAuthentication(function MapPage() {
  return <MapScreen />;
});

MapPage.getInitialProps = InitialResolver(
  /* withServerOnly: insures it only run on SSR */

  // AuthResolver(),
  Filter.Resolve,
  Filter.Daily.Resolve,

  Filter.Map.Resolve(
    "/map/daily-rent",
  ) /* Category.Resolve: Resolves if path=/category/page? */,
  (ctx, $res) => {
    const [category] = ctx?.query?.params || [];
    if (!category) return ctx.redirect(encodeURI("/map/daily-rent/عقارات"));
    return {};
  },

  Category.Resolve,

  /* City.Resolve: Resolves if path=/category/city/page? */
  City.Resolve,

  /* Direction.Resolve: Resolves if path=/category/city/direction/page? */
  Direction.Resolve,

  /* District.Resolve: Resolves if path=/category/riyadhOrJeddah/direction/district/page?
   * District.Resolve: Resolves if path=/category/city/district/page?
   * */
  District.Resolve,

  /* Listing.Resolve: Resolves if path=/category/riyadhOrJeddah/direction/district/listing
   * Listing.Resolve: Resolves if path=/category/city/district/listing
   * */
  Listing.Resolve,
  (ctx) => {
    return { path: ctx.path, error: { msg: "Not Found" } };
  },
);
MapPage.getLayout = ({ children }) => (
  <div dir="rtl" className={css.root}>
    <div className={css.filters}>
      <Filters.Layout />
    </div>
    <AppLayout hideFooter>
      <div className={`${css.map} ${css.fullScreen}`}>{children}</div>
    </AppLayout>
  </div>
);
export default MapPage;
